/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Public language-detection API. Re-exports the normalization helpers and
 * provides the Chrome Translate guard plus the i18next detector.
 */

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, normalizeLanguageCode } from './language-normalization';

export {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  normalizeLanguageCode,
  mapBaseLanguageToSupported,
} from './language-normalization';

/**
 * Detects if Chrome Translate (or similar browser translation) is active
 * by checking for telltale signs: class="translated-*" or google translate font elements.
 *
 * @returns {boolean} True if browser translation is detected
 */
export function isBrowserTranslationActive(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const html = document.documentElement;
  const hasTranslatedClass = html.classList.contains('translated-ltr') ||
    html.classList.contains('translated-rtl');
  const hasGoogleTranslateEl = document.querySelector('.skiptranslate') !== null;

  return hasTranslatedClass || hasGoogleTranslateEl;
}

/**
 * Determines whether a mutation record targets the `<html lang>` attribute.
 *
 * @param {MutationRecord} mutation - The observed mutation record.
 * @returns {boolean} True when the mutation changed the `lang` attribute.
 */
function isLangAttributeMutation(mutation: MutationRecord): boolean {
  return mutation.type === 'attributes' && mutation.attributeName === 'lang';
}

/**
 * Reads the current `<html lang>` value and, when it is an unsupported code
 * (i.e. Chrome Translate hijacked it), returns the normalized supported locale.
 *
 * @returns {string | null} The normalized locale to restore, or null when the
 * current language is empty or already supported.
 */
function resolveHijackedLanguage(): string | null {
  const currentLang = document.documentElement.lang;
  if (!currentLang) return null;
  if (SUPPORTED_LANGUAGES.includes(currentLang as any)) return null;
  return normalizeLanguageCode(currentLang);
}

/**
 * Handles a batch of mutation records, invoking the callback for the first
 * `lang` mutation that reveals a hijacked (unsupported) language.
 *
 * @param {MutationRecord[]} mutations - Observed mutation records.
 * @param {function} onLanguageHijacked - Callback invoked with the normalized language.
 * @returns {void}
 */
function handleLangMutations(
  mutations: MutationRecord[],
  onLanguageHijacked: (normalizedLang: string) => void
): void {
  const normalized = mutations
    .filter(isLangAttributeMutation)
    .map(resolveHijackedLanguage)
    .find((lang): lang is string => lang !== null);
  if (normalized) onLanguageHijacked(normalized);
}

/**
 * Creates a MutationObserver that watches for Chrome Translate mutations on `<html lang>`.
 * When Chrome changes `pt-BR` to `pt`, this guard normalizes it back and triggers
 * an i18n language change to the correct locale.
 *
 * @param {function} onLanguageHijacked - Callback invoked with the normalized language
 * @returns {{ observe: () => void, disconnect: () => void }} Guard controller
 */
export function createChromeTranslateGuard(
  onLanguageHijacked: (normalizedLang: string) => void
): { observe: () => void; disconnect: () => void } {
  let observer: MutationObserver | null = null;

  const observe = () => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }
    observer = new MutationObserver((mutations) =>
      handleLangMutations(mutations, onLanguageHijacked)
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
    });
  };

  const disconnect = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  return { observe, disconnect };
}

/**
 * Last-resort language detector for i18next.
 *
 * It runs only after `querystring` (`?lang=`) and `localStorage` found nothing,
 * which means the visitor has never chosen a language and did not ask for one
 * in the URL. That visitor gets pt-BR. `navigator.language` is deliberately not
 * consulted: search and AI crawlers render as en-US, and reading them used to
 * serve the English homepage of a Brazil-only product.
 */
export const customLanguageDetector = {
  name: 'customLanguageDetector',
  lookup() {
    return DEFAULT_LANGUAGE;
  },
  cacheUserLanguage(lng: string) {
    // This is handled by i18next-browser-languagedetector's localStorage cache
  },
};
